/* MailBakes — postage in the basket.
   ---------------------------------------------------------------------------
   The product page charges everyone the headline rate. This is where that gets
   corrected, because the discounts depend on things that are only knowable
   once the whole basket exists: how many cards there are, and how many of them
   are going to the same house.

   The rules, in the order they are applied to each card:

     1. Cards are grouped by delivery address.
     2. A card on the STANDARD service in a group of two or more takes the
        same-address rate for that group size (£3.00 each for two, £2.80 for
        three, and so on). Those are real parcels going in one envelope, so
        they genuinely cost us less to post.
     3. Anything else — a card on its own, or one on an upgraded service —
        takes the quantity discount instead, worked out from the total number
        of cards in the basket (5% at three, 8% at five, 10% at ten).
     4. Never both. The same-address rate replaces the percentage rather than
        stacking with it.

   Upgrades keep their full price per card because Guaranteed and Special
   Delivery cost us full price per parcel however many are in the envelope.

   Every price comes from a variant of the hidden Delivery product, so the tiers
   here are read off the variant titles rather than written down twice. Change a
   price in Shopify and this follows it. Add a tier and this finds it.
   --------------------------------------------------------------------------- */
(function () {
  'use strict';

  var cart = document.querySelector('[data-mb-cart]');
  if (!cart) return;

  var PRODUCT = '/products/mb-delivery.js';
  var DELIVERY_HANDLE = 'mb-delivery';

  /* Belt and braces against a loop: this rewrites the basket and then reloads,
     so a bug that always found work to do would spin forever.

     Aug 2026. This used to skip the ENTIRE pass on the load after any rewrite:

       if (sessionStorage.getItem(FLAG) === '1') { ...; return; }

     which throws away a real correction whenever the customer's next action
     lands on that one skipped load. Delete a card straight after the basket
     rebalanced itself and the survivor keeps the two-to-one-address rate it is
     no longer entitled to, because the pass that would have put it back on the
     headline rate never ran.

     The guard is now a burst counter instead. Consecutive rewrites are only
     abandoned if there are three of them inside fifteen seconds, which is a
     runaway; anything slower is the customer editing their basket and is
     allowed through. */
  var FLAG = 'mbDeliveryBurst';
  var BURST_MS = 15000;
  var BURST_MAX = 3;
  var burst = null;
  try { burst = JSON.parse(sessionStorage.getItem(FLAG) || 'null'); } catch (e) { burst = null; }
  var startedAt = Date.now();
  if (burst && startedAt - burst.t > BURST_MS) burst = null;   // stale: forget it
  if (burst && burst.n >= BURST_MAX) return;

  function noteRewrite() {
    try {
      sessionStorage.setItem(FLAG, JSON.stringify({
        t: burst ? burst.t : startedAt,
        n: (burst ? burst.n : 0) + 1
      }));
    } catch (e) { /* private mode: the guard is a nicety, the fix is not */ }
  }

  function json(url, options) {
    return fetch(url, Object.assign({ headers: { Accept: 'application/json' } }, options || {}))
      .then(function (r) {
        if (!r.ok) throw new Error('Request failed');
        return r.json();
      });
  }

  /* ---------------------------------------------------------------- tiers */

  /* Variant titles carry their own rules:
       "RM Tracked 24"                        the headline rate
       "RM Tracked 24 — 3 to one address"     same-address tier, three or more
       "RM Tracked 24 — 5+ order"             quantity tier, five or more
     Parsed rather than hard-coded so a new tier is a Shopify edit, not a deploy. */
  function readTiers(variants) {
    var services = {};

    variants.forEach(function (v) {
      var title = String(v.title);
      var dash = title.indexOf('—');
      var name = (dash === -1 ? title : title.slice(0, dash)).trim();
      var rule = dash === -1 ? '' : title.slice(dash + 1).trim();

      var s = services[name] || (services[name] = { base: null, address: [], quantity: [], multi: [] });

      if (!rule) { s.base = v; return; }

      var multi = /^Multi-Card (\d+)-(\d+)$/i.exec(rule);
      if (multi) { s.multi.push({ min: +multi[1], max: +multi[2], variant: v }); return; }

      var address = /^(\d+)\+?\s+to one address$/i.exec(rule);
      if (address) { s.address.push({ min: parseInt(address[1], 10), variant: v }); return; }

      var quantity = /^(\d+)\+\s+order$/i.exec(rule);
      if (quantity) s.quantity.push({ min: parseInt(quantity[1], 10), variant: v });
    });

    Object.keys(services).forEach(function (name) {
      var s = services[name];
      var bigFirst = function (a, b) { return b.min - a.min; };
      s.address.sort(bigFirst);
      s.quantity.sort(bigFirst);
    });

    return services;
  }

  /** The best tier at or below `count`, or the headline rate. */
  function pick(list, count, fallback) {
    for (var i = 0; i < list.length; i++) {
      if (count >= list[i].min) return list[i].variant;
    }
    return fallback;
  }

  /* ------------------------------------------------------------ addresses */

  /* Two cards count as one delivery when the postcode and the first line
     match. Deliberately not the name: "Mum" and "Margaret Talbot" at the same
     house is one envelope, and charging twice for it would be wrong. */
  function addressKey(props) {
    var post = String(props['Shipping Postcode'] || '').toUpperCase().replace(/\s+/g, '');
    var line = String(props['Shipping Address Line 1'] || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    if (!post || !line) return null;   // incomplete: it groups with nothing
    return post + '|' + line;
  }

  /* --------------------------------------------------------------- basket */

  function scan(items, services) {
    var cards = [];
    var deliveryByGroup = {};

    items.forEach(function (item, index) {
      var props = item.properties || {};
      if (item.handle === DELIVERY_HANDLE && props._mb_parent_group) {
        deliveryByGroup[props._mb_parent_group] = { item: item, index: index };
        return;
      }
      if (props._mb_group) {
        cards.push({
          item: item,
          quantity: Math.max(1, parseInt(item.quantity, 10) || 1),
          group: props._mb_group,
          key: addressKey(props),
          service: String(props['Delivery Option'] || '').trim()
        });
      }
    });

    /* How many cards share each address. A card with no usable address is on
       its own — we cannot claim it shares an envelope with anything. */
    var perAddress = {};
    cards.forEach(function (c) {
      if (!c.key) return;
      perAddress[c.key] = (perAddress[c.key] || 0) + c.quantity;
    });

    var total = cards.reduce(function (sum, c) { return sum + c.quantity; }, 0);

    cards.forEach(function (c) {
      var s = services[c.service] || services[Object.keys(services)[0]];
      if (!s || !s.base) { c.target = null; return; }

      var shared = c.key ? perAddress[c.key] : 1;

      /* A service qualifies for the same-address rate if Shopify has one for
         it. Standard does; the guaranteed services do not, because they cost
         us full price per parcel however many share an envelope. Read from the
         data rather than hard-coded, so adding same-address rates to another
         service is a Shopify edit and nothing else.

         Never both — see the note at the top of the file. */
      c.target = (s.address.length && shared >= 2)
        ? pick(s.address, shared, s.base)
        : pick(s.quantity, total, s.base);

      c.deliveryQuantity = c.quantity;
      // A multi-card selection is one parcel to its single delivery address.
      var parcel = s.multi.filter(function (t) { return c.quantity >= t.min && c.quantity <= t.max; })[0];
      if (parcel) { c.target = parcel.variant; c.deliveryQuantity = 1; }
      c.discounted = c.target.price * c.deliveryQuantity < s.base.price * c.quantity;
      c.delivery = deliveryByGroup[c.group] || null;
      c.shared = shared;
    });

    /* Postage lines whose card has gone.
       ------------------------------------------------------------------
       Nothing else in the basket will ever clear these. apply() walks the
       CARDS, so a delivery line pointing at a group that no longer has a card
       is invisible to it, and mb-cart.js only sweeps children when the removal
       goes through its own Remove button — a card taken out from the drawer,
       the default cart form, or a quantity set to zero leaves its postage
       behind. The line then sits in the basket with no row of its own and no
       way for the customer to reach it: two cards to two addresses shows £8,
       one card is deleted, and delivery is still £8.

       Swept here because this is the one place that knows which groups are
       real, and it already reloads after changing the basket. */
    var live = {};
    cards.forEach(function (c) { live[c.group] = true; });

    var orphans = Object.keys(deliveryByGroup)
      .filter(function (group) { return !live[group]; })
      .map(function (group) { return deliveryByGroup[group].item; });

    return { cards: cards, orphans: orphans };
  }

  /* ----------------------------------------------------------------- work */

  function apply(cards, orphans) {
    var remove = [];
    var add = [];

    /* Orphans first: they are pure subtraction, and clearing them can change
       nothing else, so there is no interaction with the tier maths below. */
    (orphans || []).forEach(function (item) { remove.push(item.key); });

    cards.forEach(function (c) {
      if (!c.target) return;

      if (!c.delivery) {
        /* A card with no postage on it at all — added before postage existed,
           or an add that failed. It cannot be posted without one. */
        add.push({ id: c.target.id, quantity: c.deliveryQuantity, properties: { _mb_parent_group: c.group } });
        return;
      }

      if (c.delivery.item.variant_id === c.target.id && c.delivery.item.quantity === c.deliveryQuantity) return;   // already right

      remove.push(c.delivery.item.key);
      add.push({ id: c.target.id, quantity: c.deliveryQuantity, properties: { _mb_parent_group: c.group } });
    });

    if (!remove.length && !add.length) return Promise.resolve(false);

    /* Removals first, keyed by line item rather than position — positions
       shift underneath you as lines disappear. */
    var chain = remove.reduce(function (p, key) {
      return p.then(function () {
        return json('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity: 0 })
        });
      });
    }, Promise.resolve());

    return chain
      .then(function () {
        if (!add.length) return null;
        return json('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: add })
        });
      })
      .then(function () { return true; });
  }

  /* --------------------------------------------------------------- notice */

  /* Why the number changed, said in the basket rather than left as a surprise
     at checkout. */
  function explain(cards) {
    var saved = cards.filter(function (c) { return c.shared >= 2 && c.discounted; });
    if (!saved.length) return;

    var note = document.getElementById('mb-ship-note');
    if (!note) {
      note = document.createElement('p');
      note.id = 'mb-ship-note';
      note.className = 'mb-ship-note';
      var anchor = document.getElementById('mb-missing') || cart.firstElementChild;
      if (anchor && anchor.parentNode) anchor.parentNode.insertBefore(note, anchor);
      var css = document.createElement('style');
      css.textContent = '.mb-ship-note{margin:0 0 14px;padding:10px 13px;border-radius:10px;' +
        'background:var(--mb-pink-soft,#FDEAF2);color:var(--mb-pink-deep,#C31A63);' +
        'font-size:13.5px;font-weight:650}';
      document.head.appendChild(css);
    }

    var addresses = {};
    saved.forEach(function (c) { addresses[c.key] = c.shared; });
    var houses = Object.keys(addresses).length;

    note.textContent = houses === 1
      ? 'Cheaper postage applied — ' + addresses[Object.keys(addresses)[0]] +
        ' cards going to the same address travel in one envelope.'
      : 'Cheaper postage applied to ' + houses + ' addresses with more than one card going to them.';
  }

  /* ----------------------------------------------------------------- run */

  Promise.all([json(PRODUCT), json('/cart.js')])
    .then(function (both) {
      var product = both[0];
      var basket = both[1];
      if (!product || !product.variants || !basket || !basket.items) return;

      var services = readTiers(product.variants);
      var found = scan(basket.items, services);
      var cards = found.cards;
      var orphans = found.orphans;

      /* An empty basket used to return here. It cannot any more: the case
         where every card has been deleted and only the postage is left is
         precisely the one the customer complained about — "even after the
         second card has been deleted, the additional £4 delivery charge can
         remain in the basket". No cards AND no orphans is the real nothing. */
      if (!cards.length && !orphans.length) return;

      explain(cards);

      return apply(cards, orphans).then(function (changed) {
        if (!changed) return;
        /* The page is rendered from the basket, so it has to be redrawn. */
        noteRewrite();
        window.location.reload();
      });
    })
    .catch(function () {
      /* Any failure leaves the basket exactly as the customer built it, at the
         headline rate. Charging full price by accident is recoverable; a
         half-rewritten basket is not. */
    });
})();